import axios from "axios";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import { server } from "../../../server";

const emailQuillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link"],
    ["clean"],
  ],
};

const emailQuillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "align",
  "link",
];

const PASSWORD_PLACEHOLDER = "********";

const defaultEmailSettings = {
  smtpHost: "",
  smtpPort: 465,
  smtpUser: "",
  smtpPassword: "",
  fromEmail: "",
  fromName: "",
  enquirySubjectPrefix: "Re: Your enquiry – ",
};

function resolveTemplateDraft(definition, saved = {}) {
  return {
    subject: saved.subject || definition?.defaultSubject || "",
    title: saved.title || definition?.defaultTitle || "",
    body: saved.body || definition?.defaultBody || "",
  };
}

const TEMPLATE_GROUPS = ["Orders", "Account", "Seller", "Notifications"];

function buildPreviewHtml(draft, definition, emailBranding = null) {
  let html = draft.body || "";
  const branded = emailBranding?.placeholderExamples || {};
  const samples = {
    "{{siteName}}": emailBranding?.siteName || "Veteran Airsoft",
    ...branded,
  };
  (definition?.placeholders || []).forEach((ph) => {
    if (samples[ph.key]) return;
    if (ph.example && ph.example !== "[auto]" && ph.example !== "[site]") {
      samples[ph.key] = ph.example;
    } else if (ph.key.includes("Table") || ph.key.includes("Block") || ph.key.includes("Address") || ph.key.includes("Button") || ph.key.includes("Info") || ph.key.includes("Items") || ph.key.includes("Reason") || ph.key.includes("Detail") || ph.key.includes("Line")) {
      samples[ph.key] = `<div style="padding:8px 12px;background:#f1f5f9;border:1px dashed #cbd5e1;border-radius:6px;color:#64748b;font-size:12px;">${ph.label} (auto-generated when sent)</div>`;
    }
  });
  Object.entries(samples).forEach(([key, value]) => {
    html = html.split(key).join(value);
  });
  return html;
}

const EmailSettings = () => {
  const [settings, setSettings] = useState(defaultEmailSettings);
  const [templateDefinitions, setTemplateDefinitions] = useState([]);
  const [emailBranding, setEmailBranding] = useState(null);
  const [emailTemplates, setEmailTemplates] = useState({});
  const [selectedTemplateKey, setSelectedTemplateKey] = useState("order_confirmation");
  const [activeTab, setActiveTab] = useState("smtp");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const quillRef = useRef(null);

  const selectedDefinition = useMemo(
    () => templateDefinitions.find((item) => item.key === selectedTemplateKey) || null,
    [templateDefinitions, selectedTemplateKey]
  );

  const templateGroups = useMemo(() => {
    const groups = TEMPLATE_GROUPS.filter((group) =>
      templateDefinitions.some((item) => item.group === group)
    );
    const other = templateDefinitions.filter(
      (item) => !TEMPLATE_GROUPS.includes(item.group)
    );
    if (other.length > 0) groups.push("Other");
    return groups;
  }, [templateDefinitions]);

  const currentDraft = useMemo(
    () => resolveTemplateDraft(selectedDefinition, emailTemplates[selectedTemplateKey]),
    [selectedDefinition, emailTemplates, selectedTemplateKey]
  );

  const previewHtml = useMemo(
    () => (selectedDefinition ? buildPreviewHtml(currentDraft, selectedDefinition, emailBranding) : ""),
    [currentDraft, selectedDefinition, emailBranding]
  );

  const previewTitle = useMemo(() => {
    let title = currentDraft.title || "Email preview";
    const branded = emailBranding?.placeholderExamples || {};
    title = title.split("{{siteName}}").join(emailBranding?.siteName || branded["{{siteName}}"] || "Veteran Airsoft");
    (selectedDefinition?.placeholders || []).forEach((ph) => {
      const sample = branded[ph.key] || (ph.example && ph.example !== "[auto]" && ph.example !== "[site]" ? ph.example : null);
      if (sample) {
        title = title.split(ph.key).join(sample);
      }
    });
    return title;
  }, [currentDraft.title, selectedDefinition, emailBranding]);

  const loadSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await axios.get(`${server}/options/global`, {
        withCredentials: true,
      });
      const es = data?.options?.emailSettings || {};
      setSettings({
        ...defaultEmailSettings,
        smtpHost: es.smtpHost ?? "",
        smtpPort: Number(es.smtpPort) || 465,
        smtpUser: es.smtpUser ?? "",
        smtpPassword: es.smtpPassword === PASSWORD_PLACEHOLDER ? "" : (es.smtpPassword ?? ""),
        fromEmail: es.fromEmail ?? "",
        fromName: es.fromName ?? "",
        enquirySubjectPrefix: es.enquirySubjectPrefix ?? "Re: Your enquiry – ",
      });
      setTemplateDefinitions(Array.isArray(data?.emailTemplateDefinitions) ? data.emailTemplateDefinitions : []);
      setEmailBranding(data?.emailBranding && typeof data.emailBranding === "object" ? data.emailBranding : null);
      setEmailTemplates(es.emailTemplates && typeof es.emailTemplates === "object" ? es.emailTemplates : {});
      if (data?.emailTemplateDefinitions?.[0]?.key) {
        setSelectedTemplateKey((prev) =>
          data.emailTemplateDefinitions.some((item) => item.key === prev)
            ? prev
            : data.emailTemplateDefinitions[0].key
        );
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to load email settings. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateTemplateField = (field, value) => {
    if (!selectedTemplateKey) return;
    setEmailTemplates((prev) => ({
      ...prev,
      [selectedTemplateKey]: {
        ...resolveTemplateDraft(selectedDefinition, prev[selectedTemplateKey]),
        [field]: value,
      },
    }));
  };

  const insertPlaceholder = (token) => {
    if (!token) return;
    const editor = quillRef.current?.getEditor?.();
    if (editor) {
      const range = editor.getSelection(true);
      const prefix = range.index > 0 ? "\n" : "";
      editor.insertText(range.index, `${prefix}${token}\n`, { bold: true });
      editor.setSelection(range.index + prefix.length + token.length + 1);
      return;
    }
    const spacer = currentDraft.body ? "<p><br></p>" : "";
    updateTemplateField("body", `${currentDraft.body || ""}${spacer}<p><strong>${token}</strong></p>`);
  };

  const resetCurrentTemplate = () => {
    if (!selectedDefinition) return;
    setEmailTemplates((prev) => ({
      ...prev,
      [selectedTemplateKey]: {
        subject: selectedDefinition.defaultSubject || "",
        title: selectedDefinition.defaultTitle || "",
        body: selectedDefinition.defaultBody || "",
      },
    }));
    setEditorKey((key) => key + 1);
    toast.info("Template reset to default wording. Save to apply.");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSaving(true);
      const payload = {
        emailSettings: {
          smtpHost: (settings.smtpHost || "").trim(),
          smtpPort: Number(settings.smtpPort) || 465,
          smtpUser: (settings.smtpUser || "").trim(),
          fromEmail: (settings.fromEmail || "").trim(),
          fromName: (settings.fromName || "").trim(),
          enquirySubjectPrefix: (settings.enquirySubjectPrefix || "").trim(),
          emailTemplates,
        },
      };
      if (settings.smtpPassword && settings.smtpPassword !== PASSWORD_PLACEHOLDER) {
        payload.emailSettings.smtpPassword = settings.smtpPassword;
      }
      const { data } = await axios.put(`${server}/options/global`, payload, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });
      if (Array.isArray(data?.emailTemplateDefinitions)) {
        setTemplateDefinitions(data.emailTemplateDefinitions);
      }
      if (data?.emailBranding && typeof data.emailBranding === "object") {
        setEmailBranding(data.emailBranding);
      }
      const savedTemplates = data?.options?.emailSettings?.emailTemplates;
      if (savedTemplates && typeof savedTemplates === "object") {
        setEmailTemplates(savedTemplates);
      }
      toast.success("Email settings saved.");
      setSettings((prev) => ({ ...prev, smtpPassword: "" }));
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to save email settings. Please try again."
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="py-20 text-center text-slate-500">Loading email settings…</div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-4">
        <button
          type="button"
          onClick={() => setActiveTab("smtp")}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
            activeTab === "smtp"
              ? "bg-[#38513b] text-white"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          SMTP & sender
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("templates")}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
            activeTab === "templates"
              ? "bg-[#38513b] text-white"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          Email templates
        </button>
      </div>

      {activeTab === "smtp" ? (
        <>
          <div>
            <h3 className="text-base font-semibold text-slate-800 mb-1">SMTP (outgoing mail)</h3>
            <p className="text-sm text-slate-500 mb-4">
              Used for all outgoing emails (activation, order notifications, customer enquiry replies).
              Leave blank to use environment variables (SMPT_HOST, SMPT_MAIL, SMPT_PASSWORD, SMPT_PORT).
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">SMTP Host</label>
                <input
                  type="text"
                  value={settings.smtpHost}
                  onChange={(e) => handleChange("smtpHost", e.target.value)}
                  placeholder="e.g. smtp.gmail.com"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#38513b] focus:border-[#38513b]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">SMTP Port</label>
                <input
                  type="number"
                  min={1}
                  max={65535}
                  value={settings.smtpPort}
                  onChange={(e) => handleChange("smtpPort", e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#38513b] focus:border-[#38513b]"
                />
                <p className="text-xs text-slate-500 mt-1">Usually 465 (SSL) or 587 (TLS)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">SMTP User / Email</label>
                <input
                  type="text"
                  value={settings.smtpUser}
                  onChange={(e) => handleChange("smtpUser", e.target.value)}
                  placeholder="e.g. noreply@yoursite.com"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#38513b] focus:border-[#38513b]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">SMTP Password</label>
                <input
                  type="password"
                  value={settings.smtpPassword}
                  onChange={(e) => handleChange("smtpPassword", e.target.value)}
                  placeholder="Leave blank to keep current"
                  autoComplete="new-password"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#38513b] focus:border-[#38513b]"
                />
                <p className="text-xs text-slate-500 mt-1">For Gmail use an App Password, not your normal password.</p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-base font-semibold text-slate-800 mb-1">Customer enquiries (from address & subject)</h3>
            <p className="text-sm text-slate-500 mb-4">
              Shown as the sender when replying to contact form enquiries. Subject prefix is used when no custom subject is entered.
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">From email</label>
                <input
                  type="text"
                  value={settings.fromEmail}
                  onChange={(e) => handleChange("fromEmail", e.target.value)}
                  placeholder="e.g. support@yoursite.com (defaults to SMTP user)"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#38513b] focus:border-[#38513b]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">From name</label>
                <input
                  type="text"
                  value={settings.fromName}
                  onChange={(e) => handleChange("fromName", e.target.value)}
                  placeholder="e.g. Veteran Airsoft"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#38513b] focus:border-[#38513b]"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Default subject prefix for enquiry replies</label>
                <input
                  type="text"
                  value={settings.enquirySubjectPrefix}
                  onChange={(e) => handleChange("enquirySubjectPrefix", e.target.value)}
                  placeholder="Re: Your enquiry – "
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#38513b] focus:border-[#38513b]"
                />
                <p className="text-xs text-slate-500 mt-1">Final subject will be: prefix + enquiry type (e.g. &quot;Re: Your enquiry – General Enquiries&quot;)</p>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="space-y-6">
          <div>
            <h3 className="text-base font-semibold text-slate-800 mb-1">Transactional emails</h3>
            <p className="text-sm text-slate-500">
              Edit wording with the visual editor — no HTML required. Insert dynamic fields (buyer name, order ID, cart table, etc.) using the buttons below the subject line.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email type</label>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <select
                value={selectedTemplateKey}
                onChange={(e) => setSelectedTemplateKey(e.target.value)}
                className="w-full sm:flex-1 min-w-0 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#38513b] focus:border-[#38513b]"
              >
                {templateGroups.map((group) => (
                  <optgroup key={group} label={group}>
                    {templateDefinitions
                      .filter((item) =>
                        group === "Other" ? !TEMPLATE_GROUPS.includes(item.group) : item.group === group
                      )
                      .map((item) => (
                        <option key={item.key} value={item.key}>
                          {item.label}
                        </option>
                      ))}
                  </optgroup>
                ))}
              </select>
              <button
                type="button"
                onClick={resetCurrentTemplate}
                className="shrink-0 whitespace-nowrap rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
              >
                Reset to default
              </button>
            </div>
            {selectedDefinition?.description ? (
              <p className="mt-2 text-xs text-slate-500 leading-relaxed">{selectedDefinition.description}</p>
            ) : null}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email subject</label>
              <input
                type="text"
                value={currentDraft.subject}
                onChange={(e) => updateTemplateField("subject", e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#38513b] focus:border-[#38513b]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email heading (title in email)</label>
              <input
                type="text"
                value={currentDraft.title}
                onChange={(e) => updateTemplateField("title", e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#38513b] focus:border-[#38513b]"
              />
            </div>
          </div>

          {selectedDefinition?.placeholders?.length > 0 ? (
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">Insert dynamic fields</p>
              <p className="text-xs text-slate-500 mb-2">
                Click to add a field at the cursor. Do not edit the {"{{…}}"} tags — they are replaced automatically when the email is sent.
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedDefinition.placeholders.map((ph) => (
                  <button
                    key={ph.key}
                    type="button"
                    onClick={() => insertPlaceholder(ph.key)}
                    title={`${ph.label} — inserts ${ph.key}`}
                    className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-900 hover:bg-emerald-100"
                  >
                    + {ph.label}
                  </button>
                ))}
              </div>
              <ul className="mt-3 space-y-1 text-xs text-slate-500">
                {selectedDefinition.placeholders.map((ph) => (
                  <li key={`${ph.key}-help`}>
                    <span className="font-mono text-slate-600">{ph.key}</span> — {ph.label}
                    {ph.example && ph.example !== "[auto]" ? ` (e.g. ${ph.example})` : ""}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email message</label>
            <div className="rounded-lg border border-slate-200 overflow-hidden bg-white [&_.ql-toolbar]:border-0 [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-slate-200 [&_.ql-container]:border-0 [&_.ql-editor]:min-h-[260px]">
              <ReactQuill
                ref={quillRef}
                key={`${selectedTemplateKey}-${editorKey}`}
                theme="snow"
                value={currentDraft.body || ""}
                onChange={(html) => updateTemplateField("body", html)}
                modules={emailQuillModules}
                formats={emailQuillFormats}
                placeholder="Write your email message here…"
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Use the toolbar for bold, lists, and links. HTML is generated for you — no code editing needed.
            </p>
          </div>

          <div>
            <p className="text-sm font-medium text-slate-700 mb-2">Preview (sample data)</p>
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="bg-[#38513b] px-4 py-3 text-white text-sm font-semibold">
                {previewTitle}
              </div>
              <div
                className="p-4 text-sm text-slate-700 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isSaving}
          className="rounded-lg bg-[#38513b] px-6 py-2.5 text-sm font-medium text-white hover:bg-[#2f4232] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? "Saving…" : "Save email settings"}
        </button>
      </div>
    </form>
  );
};

export default EmailSettings;
